// The one Share for a finished session: a picture, a message or a link,
// each sent on its own.
//
// Replaces three menu items that did overlapping things (Copy summary, Copy
// session plan, Share) plus the first "Send to a mate" dialog, which sent a
// picture and text together. People send one or the other, so the sheet asks
// which first and remembers the answer.
//
//   Picture  the card (utils/canvas/workoutCard), dark or light
//   Message  Short (the WhatsApp lines) or Full (Copy summary's breakdown),
//            ending with the site, or this workout's link when ticked
//   Link     a share link a mate can open and run, no account needed to look
//
// A workout link is made only when something is sent or copied, so opening
// the sheet publishes nothing; once made it is reused while the sheet is on
// the same session.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Loader2, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import useLocalStorage from "../../hooks/useLocalStorage";
import api from "../../services/api";
import {
  canLinkWorkout,
  metricRows,
  formatShareText,
  shareCardData,
  shareFileName,
  shareStats,
} from "../../utils/workoutShare";
import { formatSessionSummary } from "../../utils/sessionShare";
import { buildShareUrl, copyTextToClipboard } from "../../utils/shareLinks";
import {
  CARD_THEMES,
  MAX_CARD_STATS,
  drawWorkoutCard,
  hasIntervals,
  workoutCardBlob,
} from "../../utils/canvas/workoutCard";
import { shareWorkout } from "../../platform/shareWorkout";
import { cn } from "../../lib/utils";

const MODES = [
  ["picture", "Picture"],
  ["message", "Message"],
  ["link", "Link"],
];

const siteUrl = () => (typeof window !== "undefined" ? window.location.origin : "");

const RESULT_TOAST = {
  copied: {
    title: "Copied",
    description: "Paste it into WhatsApp or any chat.",
  },
  downloaded: {
    title: "Picture saved",
    description: "Attach it in your chat.",
  },
  "update-app": {
    title: "Update the app to share pictures",
    description: "Install the latest Android app from the site (Help › Android app), or send a Message instead.",
  },
  failed: { title: "Couldn't share", description: "Try Copy instead." },
};

// Two or three pill buttons, one on.
const Toggle = ({ label, options, value, onChange, className }) => (
  <div
    className={cn("grid gap-1 rounded-xl bg-muted/60 p-1", className)}
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    role="group"
    aria-label={label}
  >
    {options.map(([key, name]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={value === key}
        className={cn(
          "rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
          value === key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {name}
      </button>
    ))}
  </div>
);

/**
 * @param {object} props
 * @param {object} props.session        the history row
 * @param {Array}  [props.samples]      its 1 Hz telemetry, when already loaded
 * @param {object} [props.summaryPlan]  the plan for the Full message (with
 *   recorded times stripped when the user hides them)
 * @param {object} [props.summaryMeta]  { notes, rating, durationMinutes }
 */
// The numbers to send: one row per kind (Workout, Heart, Power…) of short
// chips, listing only what this session has. A full set greys out the rest.
const NumberPicker = ({ session, chosen, onToggle }) => {
  const rows = useMemo(() => metricRows(session), [session]);
  const keys = rows.flatMap((r) => r.metrics.map((m) => m.key));
  if (keys.length < 2) return null;
  const count = keys.filter((k) => chosen.includes(k)).slice(0, MAX_CARD_STATS).length;
  const full = count >= MAX_CARD_STATS;
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Numbers</span>
        <span className="normal-case tracking-normal">
          {count} of {MAX_CARD_STATS}
        </span>
      </div>
      <div className="grid gap-1.5" role="group" aria-label="Numbers to share">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[3.75rem_1fr] items-center gap-2">
            <span className="text-xs text-muted-foreground">{r.label}</span>
            <div className="flex flex-wrap gap-1.5">
              {r.metrics.map((m) => {
                const on = chosen.includes(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    aria-pressed={on}
                    aria-label={m.label}
                    disabled={on ? count === 1 : full}
                    onClick={() => onToggle(m.key)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-[13px] font-semibold transition-colors disabled:opacity-35",
                      on ? "border-primary bg-primary/15" : "border-transparent bg-muted",
                    )}
                  >
                    {m.short || m.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ShareSessionSheet = ({ open, onOpenChange, session, samples = null, summaryPlan = null, summaryMeta = {} }) => {
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  const [savedMode, setMode] = useLocalStorage("shareSheetMode", "picture");
  const [savedTheme, setTheme] = useLocalStorage("shareCardTheme", "dark");
  // A graph with heart rate (or power) and steps: the line, the steps, or both.
  const [savedGraph, setGraph] = useLocalStorage("shareCardGraph", "line");
  const [savedLength, setLength] = useLocalStorage("shareMessageLength", "short");
  const [linkWorkout, setLinkWorkout] = useLocalStorage("shareMessageLink", false);
  // The user's numbers (keys), or null for the defaults.
  const [savedMetrics, setMetrics] = useLocalStorage("shareMetrics", null);
  const [busy, setBusy] = useState(false);
  const [workoutUrl, setWorkoutUrl] = useState(null);
  // The list endpoint leaves the 1 Hz samples out; fetch them for the graph.
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  const id = session?.id;
  const linkable = Boolean(isAuthenticated) && canLinkWorkout(session);
  const hasFull = Boolean(summaryPlan?.children?.length);
  const modes = linkable ? MODES : MODES.filter(([k]) => k !== "link");
  const mode = modes.some(([k]) => k === savedMode) ? savedMode : "picture";
  const theme = CARD_THEMES.includes(savedTheme) ? savedTheme : "dark";
  const length = hasFull && savedLength === "full" ? "full" : "short";
  const withWorkoutLink = linkable && Boolean(linkWorkout);

  const rows = samples || session?.telemetry || (loaded?.id === id ? loaded.telemetry : null) || null;
  const fetchable = id != null && !String(id).startsWith("local-");
  const wantsSamples = mode === "picture" && !rows && fetchable && session?.telemetry_summary;

  useEffect(() => {
    if (!open || !wantsSamples) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .getSession(id)
      .then((full) => {
        if (!cancelled) setLoaded({ id, telemetry: full?.telemetry || [] });
      })
      .catch((err) => {
        console.warn("[ShareSessionSheet] telemetry fetch failed", err);
        if (!cancelled) setLoaded({ id, telemetry: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, wantsSamples, id]);

  // A link made for one session must not follow the sheet to another.
  useEffect(() => {
    setWorkoutUrl(null);
  }, [id]);

  // What's chosen now: the saved pick, or the defaults this session shows.
  const defaults = useMemo(() => (session ? shareStats(session).map((m) => m.key) : []), [session]);
  const metrics = Array.isArray(savedMetrics) && savedMetrics.length ? savedMetrics : defaults;
  const toggleMetric = (key) => {
    // Keep keys this session lacks, so a run doesn't drop your power pick.
    setMetrics(metrics.includes(key) ? metrics.filter((k) => k !== key) : [...metrics, key]);
  };
  const data = useMemo(() => (session ? shareCardData(session, rows, { metrics }) : null), [session, rows, metrics]);
  const showGraphChoice = hasIntervals(data?.graph);
  const graphMode = ["line", "intervals", "both"].includes(savedGraph) ? savedGraph : "line";
  const options = useMemo(() => ({ theme, graph: graphMode }), [theme, graphMode]);

  // Redraw the card when it or the background changes, and once the web font
  // has loaded (the first draw may use the fallback face).
  useEffect(() => {
    if (!open || mode !== "picture" || !data) return undefined;
    let cancelled = false;
    const draw = () => {
      if (!cancelled && canvasRef.current) drawWorkoutCard(canvasRef.current, data, options, 0.5);
    };
    // The canvas mounts with the dialog's content, a frame after `open`.
    const raf = requestAnimationFrame(draw);
    document.fonts?.ready?.then(draw);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [open, mode, data, options]);

  const messageFor = (url) =>
    length === "full"
      ? `${formatSessionSummary(summaryPlan, summaryMeta)}\n\n${url}`
      : formatShareText(session, { url, metrics });

  const previewText = useMemo(() => {
    if (!session) return "";
    const url = withWorkoutLink ? workoutUrl || `${siteUrl()}/?s=…` : siteUrl();
    return messageFor(url);
    // messageFor reads only the values listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, length, withWorkoutLink, workoutUrl, summaryPlan, summaryMeta, metrics]);

  if (!session) return null;

  const title = data?.facts?.title || "Workout";

  // This workout's share link, made the first time it's needed.
  const ensureWorkoutUrl = async () => {
    if (workoutUrl) return workoutUrl;
    const created = await api.createShare({
      kind: "session",
      workout_session_id: id,
      visibility: "link",
      title: session?.plan_data?.label || session?.workout_type || null,
    });
    const url = buildShareUrl(created.share_code);
    setWorkoutUrl(url);
    return url;
  };

  const finalMessage = async () => {
    if (!withWorkoutLink) return messageFor(siteUrl());
    try {
      return messageFor(await ensureWorkoutUrl());
    } catch (err) {
      console.warn("[ShareSessionSheet] couldn't make the workout link", err);
      return messageFor(siteUrl());
    }
  };

  const report = (result) => {
    if (RESULT_TOAST[result]) showToast(RESULT_TOAST[result]);
    if (result === "shared") onOpenChange(false);
  };

  const run = (fn) => async () => {
    setBusy(true);
    try {
      report(await fn());
    } catch (err) {
      console.warn("[ShareSessionSheet] share failed", err);
      report("failed");
    } finally {
      setBusy(false);
    }
  };

  const sharePicture = run(async () =>
    shareWorkout({
      image: await workoutCardBlob(data, options),
      fileName: shareFileName(session),
      title,
    }),
  );
  const shareMessage = run(async () => shareWorkout({ text: await finalMessage(), title }));
  const copyMessage = run(async () => ((await copyTextToClipboard(await finalMessage())) ? "copied" : "failed"));
  const shareLink = run(async () => shareWorkout({ text: await ensureWorkoutUrl(), title }));
  const copyLink = run(async () => ((await copyTextToClipboard(await ensureWorkoutUrl())) ? "copied" : "failed"));

  const spinner = busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share
          </DialogTitle>
          <DialogDescription className="sr-only">
            Send this session as a picture, a message or a link.
          </DialogDescription>
        </DialogHeader>

        <Toggle label="What to send" options={modes} value={mode} onChange={setMode} />

        {mode === "picture" && (
          <div className="grid gap-3">
            <div className="relative">
              <canvas
                ref={canvasRef}
                role="img"
                aria-label={`Share card for ${title}`}
                className="block aspect-[27/20] w-full rounded-xl bg-muted"
              />
              {loading && (
                <div className="absolute inset-x-0 bottom-3 flex justify-center">
                  <span className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading the graph…
                  </span>
                </div>
              )}
            </div>
            <NumberPicker session={session} chosen={metrics} onToggle={toggleMetric} />
            {showGraphChoice && (
              <Toggle
                label="Graph"
                options={[
                  ["line", data.graph.kind === "power" ? "Power" : "Heart rate"],
                  ["intervals", "Intervals"],
                  ["both", "Both"],
                ]}
                value={graphMode}
                onChange={setGraph}
              />
            )}
            <Toggle
              label="Background"
              options={[
                ["dark", "Dark"],
                ["light", "Light"],
              ]}
              value={theme}
              onChange={setTheme}
              className="w-44"
            />
            <Button type="button" className="h-12 text-base font-bold" onClick={sharePicture} disabled={busy || !data}>
              {spinner}
              Share picture
            </Button>
          </div>
        )}

        {mode === "message" && (
          <div className="grid gap-3">
            {hasFull && (
              <Toggle
                label="Length"
                options={[
                  ["short", "Short"],
                  ["full", "Full"],
                ]}
                value={length}
                onChange={setLength}
                className="w-44"
              />
            )}
            <pre
              className={cn(
                "max-h-60 overflow-auto rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground",
                length === "full" ? "font-mono text-xs" : "whitespace-pre-wrap break-words font-sans",
              )}
            >
              {previewText}
            </pre>
            {length !== "full" && <NumberPicker session={session} chosen={metrics} onToggle={toggleMetric} />}
            {linkable && (
              <label className="flex cursor-pointer items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  checked={withWorkoutLink}
                  onChange={(e) => setLinkWorkout(e.target.checked)}
                />
                Link to this workout
              </label>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" className="h-12 text-base font-bold" onClick={shareMessage} disabled={busy}>
                {spinner}
                Share
              </Button>
              <Button type="button" variant="outline" className="h-12" onClick={copyMessage} disabled={busy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        )}

        {mode === "link" && (
          <div className="grid gap-3">
            <div className="grid gap-1 rounded-lg bg-muted/40 p-3">
              <span className="font-semibold">{title}</span>
              <span className="text-sm text-muted-foreground">
                Your mate opens the link, sees the session and can run it. They don't need an account to look.
              </span>
              {workoutUrl && <code className="break-all text-xs text-muted-foreground">{workoutUrl}</code>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" className="h-12 text-base font-bold" onClick={shareLink} disabled={busy}>
                {spinner}
                Share link
              </Button>
              <Button type="button" variant="outline" className="h-12" onClick={copyLink} disabled={busy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy link
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareSessionSheet;
